import { PORTED_PAGES } from '@/pages'
import { META_BY_ROUTE } from '@/content/pages/pages.generated'
import { preload } from '@/app/moduleCache'

/*
 * Loading a route's code before the router switches to it.
 *
 * Each route's `loader` calls these, and React Router keeps the current page
 * on screen until the loader settles, so navigation goes straight from one
 * finished page to the next instead of through a blank fallback. The same
 * functions run on hover, which usually means the code is already there by
 * the time the click lands.
 */

export const loadMarketingPage = () => import('@/routes/MarketingPage')

const specs = import.meta.glob<{ default: unknown }>('/src/content/pages/data/*.ts')

export function specKey(slug: string) {
  return `spec:${slug}`
}

export function specLoader(slug: string) {
  return specs[`/src/content/pages/data/${slug}.ts`] ?? null
}

export function portedKey(route: string) {
  return `page:${route}`
}

function normalise(path: string) {
  return path === '/' ? '/' : path.replace(/\/$/, '')
}

/** Starts loading everything `path` needs; resolves when it is all in memory. */
export function preloadRoute(path: string): Promise<unknown> | null {
  const route = normalise(path)
  const ported = PORTED_PAGES[route]
  if (ported) return preload(portedKey(route), ported)
  const meta = META_BY_ROUTE.get(route)
  const spec = meta ? specLoader(meta.slug) : null
  if (meta && spec) return Promise.all([preload('route:marketing', loadMarketingPage), preload(specKey(meta.slug), spec)])
  return null
}

/*
 * A route loader that waits for the route's code. If a chunk will not load
 * (usually a tab left open across a deploy, holding file names that no longer
 * exist), it falls back to a full page load of the same URL rather than
 * leaving the visitor on an error.
 */
export async function routeLoader({ request }: { request: Request }) {
  const url = new URL(request.url)
  try {
    await preloadRoute(url.pathname)
  } catch {
    if (typeof window !== 'undefined' && window.location.href !== url.href) window.location.assign(url.href)
  }
  return null
}
